import { cn } from "@/lib/utils";
import { Empty } from "components/core/Empty";
import { HorizontalPicker } from "components/core/HorizontalPicker";
import { Tooltip } from "components/core/Tooltip";
import { IconButton } from "components/core/buttons/IconButton";
import { ContentGridMixContentQuery } from "components/domain/contents/ContentGridMixContentQuery";
import { ContentGridQuery } from "components/domain/contents/ContentGridQuery";
import {
    Section,
    SectionContent,
    SectionHeader,
    SectionTitle
} from "components/domain/contents/sections";
import { AppContext } from "contexts/AppContext";
import {
    useGetUserInterestTagsQuery,
    useSessionQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import { useRouter } from "next/router";
import { ComponentProps, useContext, useMemo } from "react";
import { zIndex } from "styles/zIndex";
import { ContentSection } from "types/app";
import { LIMIT } from "utils/buildQueryVariables";
import {
    DEFAULT_TAG_LIST,
    DEVICE_TYPE,
    MIX_CONTENT_TAG_ENUM_KOREAN,
    ORDERED_MIX_CONTENT_TAG_ENUM,
    SHINHAN_INVEST_TAG_LIST,
    TRIAL_TAG_LIST
} from "utils/enum";

import { useMixArchives } from "../hooks/useMixArchives";

export const RecentSection = ({
    contentSection,
    variables,
    className,
    ...props
}: Omit<
    ComponentProps<
        typeof ContentGridQuery<MixContentQuery, MixContentQueryVariables>
    >,
    "useQuery" | "extractor" | "updateQuery"
> & {
    className?: string;
    contentSection: ContentSection;
    variables: MixContentQueryVariables;
}) => {
    const router = useRouter();
    const { me, isLoggedIn } = useAuth();
    const { deviceType } = useContext(AppContext);
    const { archives, archiveTagEnums, archivesLoading } = useMixArchives();

    const { data: sessionData } = useSessionQuery();
    const userId = sessionData?.session?.me?.id;
    const userGroup = sessionData?.session?.me?.group_id;

    const { data: userInterestTagsData } = useGetUserInterestTagsQuery({
        variables: { userId: userId! },
        skip: !userId
    });

    const tag = useMemo(
        () =>
            ORDERED_MIX_CONTENT_TAG_ENUM.find(
                (tag) => tag === router.query.tag
            ) ?? null,
        [router]
    );

    // Apply the same filtering logic as desktop for mobile category display
    const displayTags = useMemo(() => {
        let tags: any[] = [];

        // For logged-out users, exclude Shinhan investment categories
        if (!isLoggedIn) {
            tags = ORDERED_MIX_CONTENT_TAG_ENUM.filter(
                (tag) => !SHINHAN_INVEST_TAG_LIST.includes(tag as any)
            );
        }
        // For logged-in users, apply the same logic as desktop
        // 1. 쿼리에서 유효한 태그가 있으면 해당 태그만 반환
        else if (
            typeof router.query.tag === "string" &&
            ORDERED_MIX_CONTENT_TAG_ENUM.includes(router.query.tag as any)
        ) {
            tags = [router.query.tag as any];
        }
        // 2. 유저 관심 태그가 있으면, 해당 태그 리스트 반환 (ONLY user's selected categories)
        else {
            const userTags = userInterestTagsData?.mix_user_interest_tag;
            if (userTags?.length) {
                tags = userTags.map((item) => item.tag_value as any);
            } else {
                // 3. userGroup별 분기 (only when user has no preferences)
                switch (userGroup) {
                    case "master":
                        tags = ORDERED_MIX_CONTENT_TAG_ENUM; // 전체 보기
                        break;
                    case "shinhaninvest":
                        tags = [
                            ...DEFAULT_TAG_LIST,
                            ...SHINHAN_INVEST_TAG_LIST
                        ] as any[];
                        break;
                    case "general":
                    case "thesmc":
                    case undefined:
                        tags = DEFAULT_TAG_LIST as any[];
                        break;
                    default:
                        tags = DEFAULT_TAG_LIST as any[];
                        break;
                }
            }
        }

        // Filter out trial tags from all results
        return tags.filter((tag) => !TRIAL_TAG_LIST.includes(tag as any));
    }, [isLoggedIn, router.query.tag, userInterestTagsData, userGroup]);

    return (
        <Section className={className} {...props}>
            <SectionHeader>
                <SectionTitle>
                    <div className="flex items-center gap-2">
                        <span
                            className="overflow-hidden whitespace-nowrap
                                text-text-primary"
                        >
                            콘텐츠 큐레이션
                        </span>
                        <Tooltip
                            placement="top"
                            trigger={
                                deviceType !== DEVICE_TYPE.DESKTOP
                                    ? "mousedown"
                                    : "mouseover"
                            }
                        >
                            <Tooltip.Trigger asChild={false}>
                                <IconButton
                                    name="MoreHoriz"
                                    size="medium"
                                    color="grey"
                                    variant="background"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        router.push(
                                            {
                                                ...router,
                                                hash: me
                                                    ? "#interest-tags"
                                                    : "#signin"
                                            },
                                            undefined,
                                            { scroll: false }
                                        );
                                    }}
                                    className="flex-shrink-0"
                                />
                            </Tooltip.Trigger>

                            <Tooltip.Content
                                className="max-w-[260px] break-keep rounded-md
                                    px-2.5 pb-0.5 pt-2 text-xs"
                            >
                                <p
                                    className="mb-1.5 whitespace-pre-wrap
                                        font-medium"
                                >
                                    관심 주제 설정
                                </p>
                            </Tooltip.Content>
                        </Tooltip>
                    </div>
                    {tag !== null && (
                        <span
                            className="ml-3 hidden text-text-placeholder
                                lg:inline"
                        >
                            #{MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
                        </span>
                    )}
                </SectionTitle>
            </SectionHeader>
            <SectionContent>
                <HorizontalPicker<Mix_Content_Tag_Enum_Enum | null>
                    className={cn(
                        "sticky top-[calc(var(--header-height)+1px)] block",
                        "bg-common-white-main dark:bg-common-black-main",
                        "z-[97] mb-4 pb-2 pt-[0.6rem]",
                        "lg:hidden"
                    )}
                    items={[
                        { value: null, label: "전체" },
                        ...displayTags.reduce(
                            (
                                acc: {
                                    value: Mix_Content_Tag_Enum_Enum;
                                    label: string;
                                }[],
                                value
                            ) => {
                                const label =
                                    MIX_CONTENT_TAG_ENUM_KOREAN[value];
                                if (label) {
                                    acc.push({ value, label });
                                }
                                return acc;
                            },
                            []
                        )
                    ]}
                    value={tag}
                    onChange={(value) => {
                        if (value) {
                            router.push(
                                process.env.BUILD_TARGET === "extension"
                                    ? `https://mix.day?tag=${value}`
                                    : `?tag=${value}`
                            );

                            fireEvent({
                                event: "content_category_click",
                                params: {
                                    value
                                }
                            });
                        } else {
                            router.push(
                                process.env.BUILD_TARGET === "extension"
                                    ? `https://mix.day`
                                    : `/`
                            );
                        }
                    }}
                />
                <ContentGridMixContentQuery
                    type="mixed"
                    contentSection={contentSection}
                    variables={variables}
                    limit={LIMIT.default}
                    archives={archives}
                    archiveTagEnums={archiveTagEnums}
                    archivesLoading={archivesLoading}
                    isGuest={!isLoggedIn}
                    empty={
                        <Empty
                            imgurl="/img/content-empty.png"
                            mainText="추천할 만한 콘텐츠를 열심히 찾고 있어요."
                            subText="조금만 기다려 주세요!"
                        />
                    }
                />
            </SectionContent>
        </Section>
    );
};
